/**
 * Device Fingerprint Utility
 *
 * Uses FingerprintJS to generate a stable device identifier for device tracking.
 * This fingerprint is used to:
 * - Identify returning devices during login
 * - Enable "Trust this device" functionality for MFA bypass
 * - Track device login history
 *
 * Note: The fingerprint is a hash, not PII. It cannot identify a person,
 * only distinguish between different browser/device combinations.
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Cached fingerprint promise to avoid regenerating on every call
let fpPromise: Promise<string> | null = null;

/**
 * Get a stable device fingerprint for device tracking.
 * The fingerprint is cached for the session to avoid repeated calculations.
 *
 * @returns Promise resolving to the device fingerprint (visitor ID)
 */
export async function getDeviceFingerprint(): Promise<string> {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => result.visitorId);
  }
  return fpPromise;
}

/**
 * Get a human-readable device name from the user agent.
 * Used to display device info in security settings.
 *
 * @returns A string like "Chrome on Windows" or "Safari on iPhone"
 */
export function getDeviceName(): string {
  const ua = navigator.userAgent;

  // Detect browser
  let browser = 'Unknown Browser';
  if (ua.includes('Edg/')) {
    browser = 'Edge';
  } else if (ua.includes('Chrome')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari')) {
    browser = 'Safari';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
  }

  // Detect OS
  let os = 'Unknown OS';
  if (ua.includes('iPhone')) {
    os = 'iPhone';
  } else if (ua.includes('iPad')) {
    os = 'iPad';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('Mac')) {
    os = 'Mac';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('CrOS')) {
    os = 'ChromeOS';
  }

  return `${browser} on ${os}`;
}

/**
 * Clear the cached fingerprint.
 * Useful for testing or forcing a regeneration.
 */
export function clearFingerprintCache(): void {
  fpPromise = null;
}
